import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ClayTaxi from '../site/ClayTaxi';
import Picture from '../site/Picture';
import { TIMELINE_EVENT } from '../site/ScrollRoad';
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';

// How much extra scrolling (in screen heights) the pinned scene holds for
// while the car drives from step 1 to step 3.
const DRIVE_SCREENS = 0.8;
const announce = (running) => window.dispatchEvent(new CustomEvent(TIMELINE_EVENT, { detail: { running } }));

// A full-screen scene that pins in place when it reaches the top of the
// screen (by scrolling or from the "How it works" link). While pinned, a taxi
// drives from step 1 to step 3 as you scroll, lighting each step as it
// arrives; then the page carries on. The road runs between the centres of the
// first and last number badges, measured from the DOM, so the same code
// handles the horizontal (desktop) and vertical (mobile) layouts. The page's
// left road steps aside while this car is driving.
function HowItWorks() {
  const { t } = useTranslation();
  const steps = t('home.howItWorks.steps', { returnObjects: true });
  const reducedMotion = usePrefersReducedMotion();
  const sectionRef = useRef(null);
  const listRef = useRef(null);
  const badgeRefs = useRef([]);
  const [road, setRoad] = useState(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const measure = () => {
      const list = listRef.current;
      const badges = badgeRefs.current.filter(Boolean);
      if (!list || badges.length < 2) return;
      const origin = list.getBoundingClientRect();
      const centres = badges.map((badge) => {
        const r = badge.getBoundingClientRect();
        return { x: r.left - origin.left + r.width / 2, y: r.top - origin.top + r.height / 2 };
      });
      const first = centres[0];
      const last = centres[centres.length - 1];
      const horizontal = Math.abs(last.x - first.x) > Math.abs(last.y - first.y);
      const length = horizontal ? last.x - first.x : last.y - first.y;
      setRoad({
        horizontal,
        first,
        length,
        stops: centres.map((c) => (horizontal ? c.x - first.x : c.y - first.y) / length),
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (listRef.current) observer.observe(listRef.current);
    return () => observer.disconnect();
  }, [steps.length]);

  useEffect(() => {
    if (reducedMotion) {
      setProgress(1);
      return undefined;
    }
    let frame = 0;
    let roadHidden = false;

    const update = () => {
      frame = 0;
      const section = sectionRef.current;
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const travel = rect.height - window.innerHeight;
      // 0 until the scene reaches the top of the screen, 1 once it has been
      // pinned for the whole drive.
      const p = travel > 0 ? Math.min(Math.max(-rect.top / travel, 0), 1) : 1;
      setProgress(p);
      const driving = p > 0 && p < 1;
      if (driving !== roadHidden) {
        roadHidden = driving;
        announce(driving);
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (roadHidden) announce(false);
    };
  }, [reducedMotion]);

  const reached = (i) => (road ? progress >= road.stops[i] - 0.001 : progress >= i / (steps.length - 1));

  const roadStyle = road
    ? road.horizontal
      ? { left: road.first.x, top: road.first.y - 2, width: road.length, height: 4 }
      : { left: road.first.x - 2, top: road.first.y, width: 4, height: road.length }
    : { display: 'none' };
  const carStyle = road
    ? {
        left: road.first.x,
        top: road.first.y,
        transform: road.horizontal
          ? `translate3d(${progress * road.length}px, 0, 0) translate(-50%, -50%) rotate(-90deg)`
          : `translate3d(0, ${progress * road.length}px, 0) translate(-50%, -50%)`,
      }
    : { display: 'none' };

  return (
    <section
      id="how"
      ref={sectionRef}
      className="relative bg-navy font-sans"
      style={{ height: reducedMotion ? '100svh' : `${(1 + DRIVE_SCREENS) * 100}svh` }}
    >
      <div className="sticky top-0 isolate flex h-[100svh] items-center overflow-hidden">
        <Picture
          name="stock/tirana-night-traffic-banner"
          alt=""
          width={1559}
          height={877}
          shimmer={false}
          className="absolute inset-0 -z-10 h-full w-full"
        />
        <div aria-hidden="true" className="absolute inset-0 -z-10 bg-navy/[0.82]" />

        <div className="site-container w-full">
          <h2 className="m-0 text-[28px] font-bold leading-tight tracking-tight text-white lg:text-center lg:text-[44px]">
            {t('home.howItWorks.title')}
          </h2>

          <div className="relative mt-8 lg:mt-16">
            <div aria-hidden="true" className="pointer-events-none absolute inset-0">
              <div className={`road-dash absolute rounded-full ${road?.horizontal ? '' : '[--road-dir:bottom]'}`} style={roadStyle} />
            </div>

            <ol ref={listRef} className="relative m-0 grid list-none gap-8 p-0 lg:grid-cols-3 lg:gap-8">
              {steps.map((step, i) => {
                const lit = reached(i);
                return (
                  <li key={step.title} className="flex gap-5 lg:flex-col lg:items-center lg:text-center">
                    <span
                      ref={(el) => (badgeRefs.current[i] = el)}
                      aria-hidden="true"
                      className={`relative z-20 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-solid text-lg font-extrabold transition-colors duration-300 ${
                        lit ? 'border-taxi bg-taxi text-navy' : 'border-white/30 bg-navy text-slate-300'
                      }`}
                    >
                      {i + 1}
                    </span>
                    <div className="pt-2 lg:pt-0">
                      <h3 className={`m-0 text-lg font-semibold transition-colors duration-300 lg:text-[22px] ${lit ? 'text-white' : 'text-slate-300'}`}>
                        <span className="sr-only">{t('site.how.stepLabel', { n: i + 1 })} </span>
                        {step.title}
                      </h3>
                      <p className="mb-0 mt-2 text-base leading-relaxed text-slate-300 lg:text-lg">{step.desc}</p>
                    </div>
                  </li>
                );
              })}
            </ol>

            <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-30">
              <div className="absolute will-change-transform" style={carStyle}>
                <ClayTaxi size={30} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HowItWorks;
